import * as THREE from "three";
import { useSelectionStore } from "../../stores/selectionStore";
import { getSceneState } from "../../stores/sceneStore";
import { useViewerStore } from "../../stores/viewerStore";
import { logger } from "../../utils/logger";
import {
  clearSelectedVisualMeshes,
  getSelectedVisualMeshIds,
  setSelectedVisualMeshIds,
} from "../../visual-mesh-multi-selection";
import { applyConstraintMarkerKeySelection } from "../scene/constraintMarkerVisibility";
import { getSavedSceneCameraPickUuid } from "../scene/savedCameraMarker";
import { getSavedSceneLightPickUuid } from "../scene/savedLightMarker";
import { safeTraverse } from "../scene/sceneObjectLookups";
import {
  configureRaycasterForScenePicking,
  pickBestJointHit,
  pickPriorityInspectTarget,
  preferTendonConstraintMarkerHit,
} from "../scene/scenePicking";

const inspectCandidateTypes = [
  "object",
  "body",
  "mesh",
  "geom",
  "inertial",
  "joint",
  "site",
  "camera",
  "light",
  "constraint",
  "actuator",
];

export function useSceneSelectionTargets({
  bodyRootMap,
  cameraRef,
  constraintMarkerMeshesRef,
  constraintMarkersGroupRef,
  getJointIdFromObject,
  isPhysicsRunning,
  rendererRef,
  sceneContextMenuData,
  sceneObjectsRef,
  sceneRef,
  sceneRmbContextAnchorRef,
  sceneRmbContextDraggedRef,
  selectionActions,
  setActiveSubmenu,
  setSceneAddGeomKindSubmenuOpen,
  setSceneAddJointKindSubmenuOpen,
  setSceneAddVisualGeomKindSubmenuOpen,
  setSceneContextMenuData,
  setSceneContextMenuOpen,
  setSceneContextMenuPos,
  showCollision,
  showConstraintMarkers,
  showInertials,
  showJoints,
  showSavedCameraMarkers,
  showSites,
  showVisual,
}) {
  /** Raycasts hit primitive collision child meshes; `userData.type === "collision"` lives on the parent Group. */
  const isCollisionGeomObject = (obj) => {
    let cur = obj;
    while (cur) {
      const ud = cur.userData || {};
      if (ud && ud.type === "collision") return true;
      cur = cur.parent;
    }
    return false;
  };

  const getCollisionTarget = (obj) => {
    if (!showCollision) return null;
    let cur = obj;
    while (cur) {
      const ud = cur.userData || {};
      if (ud && ud.type === "collision") {
        const bodyUuid = ud.bodyUuid || null;
        const geomUuid = ud.geomUuid || null;
        if (bodyUuid && geomUuid) {
          return { type: "geom", name: `${bodyUuid}:${geomUuid}` };
        }
      }
      cur = cur.parent;
    }
    return null;
  };

  const getVisualGeomTarget = (obj) => {
    if (!showVisual) return null;
    let cur = obj;
    while (cur) {
      const ud = cur.userData || {};
      if (ud && ud.type === "visual") {
        const bodyUuid = ud.bodyUuid || null;
        const geomUuid = ud.geomUuid || null;
        if (bodyUuid && geomUuid) {
          return { type: "geom", name: `${bodyUuid}:${geomUuid}` };
        }
      }
      if (ud && ud.type === "mesh") {
        const bodyUuid = ud.bodyUuid || null;
        const meshName = ud.meshName || null;
        const body = bodyUuid ? getSceneState()?.bodies?.[bodyUuid] : null;
        const geom = Array.isArray(body?.geoms)
          ? body.geoms.find((entry) => entry && String(entry.mesh || "") === String(meshName))
          : null;
        if (bodyUuid && geom?.uuid) {
          return { type: "geom", name: `${bodyUuid}:${geom.uuid}` };
        }
      }
      cur = cur.parent;
    }
    return null;
  };

  const getInertialTarget = (obj) => {
    if (!showInertials) return null;
    let cur = obj;
    while (cur) {
      const ud = cur.userData || {};
      if (ud && ud.type === "inertial") {
        const bodyUuid = ud.bodyUuid || null;
        const id = ud.inertialUuid || ud.inertialName;
        if (bodyUuid && id) {
          return { type: "inertial", name: `${bodyUuid}:${id}` };
        }
        if (id) {
          return { type: "inertial", name: String(id) };
        }
      }
      cur = cur.parent;
    }
    return null;
  };

  const getSiteTarget = (obj) => {
    if (!showSites) return null;
    let cur = obj;
    while (cur) {
      const ud = cur.userData || {};
      if (ud && ud.type === "site") {
        const siteId = ud.siteUuid || ud.siteName;
        if (siteId) {
          return { type: "site", name: String(siteId) };
        }
      }
      cur = cur.parent;
    }
    return null;
  };

  const findValidTarget = (obj) => {
    let actuatorNode = obj;
    while (actuatorNode) {
      const ud = actuatorNode.userData || {};
      if (ud.isActuatorMarker && ud.actuatorUuid) {
        return {
          type: "actuator",
          uuid: ud.actuatorUuid,
          parentJointUuid: ud.targetKind === "joint" ? ud.targetId || null : null,
          parentTendonUuid: ud.targetKind === "tendon" ? ud.targetId || null : null,
        };
      }
      actuatorNode = actuatorNode.parent;
    }

    const jointId = getJointIdFromObject(obj);
    if (jointId) {
      return {
        type: "joint",
        name: jointId,
        uuid: jointId,
      };
    }

    const savedCamUuid = getSavedSceneCameraPickUuid(obj);
    if (savedCamUuid) {
      return { type: "camera", uuid: savedCamUuid };
    }
    const savedLightUuid = getSavedSceneLightPickUuid(obj);
    if (savedLightUuid) {
      return { type: "light", uuid: savedLightUuid };
    }

    const collision = getCollisionTarget(obj);
    if (collision) return collision;

    const visualGeom = getVisualGeomTarget(obj);
    if (visualGeom) return visualGeom;

    const inertialTarget = getInertialTarget(obj);
    if (inertialTarget) return inertialTarget;

    if (obj.isMesh && obj.name && obj.name !== "sceneFloor" && obj.name !== "sceneGrid") {
      if (obj.userData && obj.userData.type === "mesh" && obj.userData.meshName) {
        let bodyUuid = obj.userData?.bodyUuid;
        if (!bodyUuid) {
          let parent = obj.parent;
          while (parent && !bodyUuid) {
            if (parent.userData?.bodyUuid) {
              bodyUuid = parent.userData.bodyUuid;
              break;
            }
            parent = parent.parent;
          }
        }
        if (bodyUuid && typeof bodyUuid === "string" && bodyUuid.length > 0) {
          return { type: "body", uuid: bodyUuid };
        }
        return { type: "mesh", uuid: obj.userData.meshName };
      }
      return null;
    }

    let current = obj;
    while (current) {
      if (
        current.userData &&
        current.userData.bodyUuid &&
        sceneObjectsRef.current[current.userData.bodyUuid]
      ) {
        let hasVisibleMesh = false;
        safeTraverse(current, (child) => {
          if (
            child.isMesh &&
            child.visible &&
            child.name !== "sceneFloor" &&
            child.name !== "sceneGrid" &&
            !child.userData.isJointEndpoint &&
            !child.userData.isJointArc &&
            !child.userData.isInertial &&
            showVisual
          ) {
            hasVisibleMesh = true;
          }
        });
        if (hasVisibleMesh) {
          return { type: "body", uuid: current.userData.bodyUuid };
        }
      }
      current = current.parent;
    }
    return null;
  };

  const getOwningBodyUuidForObject = (obj) => {
    if (!obj) return null;
    const bodyUuid = (obj.userData && obj.userData.bodyUuid) || null;
    if (bodyUuid) {
      if (sceneObjectsRef.current && sceneObjectsRef.current[bodyUuid]) {
        return bodyUuid;
      }
    }
    let parent = obj.parent;
    while (parent) {
      if (parent.userData && parent.userData.bodyUuid) {
        const uuid = parent.userData.bodyUuid;
        if (sceneObjectsRef.current && sceneObjectsRef.current[uuid]) {
          return uuid;
        }
      }
      parent = parent.parent;
    }
    return null;
  };

  const getResolvedFocusBodiesGateSet = () => {
    const {
      focusModeActive: fmActive,
      focusBodyName: fmBodyUuid,
      selectedBodies,
      selectedMesh: selMesh,
      selectedJoint: selJoint,
      selectedBody: selBody,
    } = useSelectionStore.getState();
    if (!fmActive) return null;

    if (Array.isArray(selectedBodies) && selectedBodies.length > 1) {
      return selectedBodies;
    }

    if (selMesh && sceneRef.current) {
      const meshObj = sceneRef.current.getObjectByName(selMesh);
      const bodyUuid = getOwningBodyUuidForObject(meshObj);
      if (bodyUuid) {
        return new Set([bodyUuid]);
      }
    }

    if (selJoint && sceneRef.current) {
      let jointViz = null;
      safeTraverse(sceneRef.current, (obj) => {
        if (
          !jointViz &&
          obj &&
          obj.userData &&
          obj.userData.isJointVizGroup &&
          obj.userData.jointUuid === selJoint
        ) {
          jointViz = obj;
          return false;
        }
        return undefined;
      });
      const bodyUuid = getOwningBodyUuidForObject(jointViz);
      if (bodyUuid) {
        return new Set([bodyUuid]);
      }
    }

    let singleResolved = fmBodyUuid || selBody || null;
    if (!singleResolved && Array.isArray(selectedBodies) && selectedBodies.length === 1) {
      singleResolved = selectedBodies[0] || null;
    }
    if (singleResolved) {
      return new Set([singleResolved]);
    }
    return null;
  };

  const collectInspectCandidatesFromIntersects = (
    validIntersects,
    constraintMarkerCandidate = null
  ) => {
    const candidates = {
      object: null,
      body: null,
      mesh: null,
      geom: null,
      inertial: null,
      joint: null,
      site: null,
      camera: null,
      light: null,
      constraint: null,
      actuator: null,
    };

    const meshDeletePreference = (candidate, hitObj) => {
      if (!candidate) return -1;
      if (candidate.selectionType === "geom") return 2;
      if (hitObj?.isMesh && hitObj.userData?.type === "mesh" && hitObj.userData.meshName) return 1;
      if (
        hitObj?.isMesh &&
        hitObj.userData?.type === "collision" &&
        hitObj.userData?.collisionGeomKind === "mesh"
      ) {
        return 0;
      }
      return 0;
    };

    let bestMeshCandidate = null;
    let bestMeshPref = -1;
    let bestMeshDist = Infinity;

    let owningBodyUuid = null;
    for (const intersect of validIntersects) {
      const obj = intersect.object;
      const bodyUuid = getOwningBodyUuidForObject(obj);
      if (!owningBodyUuid && bodyUuid) owningBodyUuid = bodyUuid;
      if (!candidates.actuator) {
        const actuatorTarget = findValidTarget(obj);
        if (actuatorTarget?.type === "actuator") candidates.actuator = actuatorTarget;
      }
      if (!candidates.body && bodyUuid) {
        candidates.body = { type: "body", uuid: bodyUuid };
      }
      if (!candidates.object && bodyUuid) {
        const rootBodyUuid = bodyRootMap[bodyUuid] || bodyUuid;
        candidates.object = { type: "object", uuid: rootBodyUuid };
      }

      let nextMeshCandidate = null;
      if (obj?.isMesh && obj?.userData?.type === "mesh" && obj?.userData?.meshName) {
        nextMeshCandidate = { type: "mesh", uuid: obj.userData.meshName };
      } else {
        const visualGeomTarget = getVisualGeomTarget(obj);
        if (visualGeomTarget) {
          nextMeshCandidate = {
            ...visualGeomTarget,
            selectionType: "geom",
          };
        }
      }
      if (nextMeshCandidate) {
        const pref = meshDeletePreference(nextMeshCandidate, obj);
        const d = intersect.distance ?? Infinity;
        if (
          pref > bestMeshPref ||
          (pref === bestMeshPref && pref >= 1 && d < bestMeshDist - 1e-9)
        ) {
          bestMeshPref = pref;
          bestMeshCandidate = nextMeshCandidate;
          bestMeshDist = d;
        }
      }

      if (!candidates.geom) {
        const geomTarget = getCollisionTarget(obj);
        if (geomTarget) candidates.geom = geomTarget;
      }
      if (!candidates.inertial) {
        const inertialTarget = getInertialTarget(obj);
        if (inertialTarget) candidates.inertial = inertialTarget;
      }
      if (!candidates.site) {
        const siteTarget = getSiteTarget(obj);
        if (siteTarget) candidates.site = siteTarget;
      }
    }
    candidates.mesh = bestMeshCandidate;
    const jointPick = pickBestJointHit(validIntersects, findValidTarget);
    if (jointPick) {
      candidates.joint = { type: "joint", uuid: jointPick.uuid, name: jointPick.uuid };
    }
    let savedCameraUuid = null;
    let savedLightUuid = null;
    for (const intersect of validIntersects) {
      const uuid = getSavedSceneCameraPickUuid(intersect.object);
      if (uuid) {
        savedCameraUuid = uuid;
        break;
      }
    }
    if (savedCameraUuid) {
      candidates.camera = { type: "camera", uuid: savedCameraUuid };
    }
    for (const intersect of validIntersects) {
      const uuid = getSavedSceneLightPickUuid(intersect.object);
      if (uuid) {
        savedLightUuid = uuid;
        break;
      }
    }
    if (savedLightUuid) {
      candidates.light = { type: "light", uuid: savedLightUuid };
    }
    if (constraintMarkerCandidate?.key) {
      candidates.constraint = { type: "constraint", key: constraintMarkerCandidate.key };
    }
    return { candidates, owningBodyUuid };
  };

  const getMeshUuidFromSelectionCandidate = (candidate) => {
    if (!candidate) return null;
    if (candidate.uuid) return String(candidate.uuid);
    if (!candidate.name) return null;

    const geomKey = String(candidate.name);
    const sep = geomKey.indexOf(":");
    if (sep <= 0) return null;
    const bodyUuid = geomKey.slice(0, sep);
    const geomUuid = geomKey.slice(sep + 1);
    const body = getSceneState()?.bodies?.[bodyUuid];
    const geom = Array.isArray(body?.geoms)
      ? body.geoms.find((entry) => entry && String(entry.uuid) === geomUuid)
      : null;
    return geom?.mesh ? String(geom.mesh) : null;
  };

  const applyVisualMeshMultiSelection = (candidate) => {
    const meshUuid = getMeshUuidFromSelectionCandidate(candidate);
    if (!meshUuid) return false;

    const selection = useSelectionStore.getState();
    const nextSelection = new Set(getSelectedVisualMeshIds());
    if (selection.selectedMesh) {
      nextSelection.add(String(selection.selectedMesh));
    }
    if (selection.selectedGeom) {
      const selectedGeomMeshUuid = getMeshUuidFromSelectionCandidate({
        name: selection.selectedGeom,
      });
      if (selectedGeomMeshUuid) {
        nextSelection.add(selectedGeomMeshUuid);
      }
    }
    if (nextSelection.has(meshUuid)) {
      nextSelection.delete(meshUuid);
    } else {
      nextSelection.add(meshUuid);
    }
    setSelectedVisualMeshIds(nextSelection);

    if (nextSelection.size === 1) {
      selection.selectMesh([...nextSelection][0]);
    } else {
      selection.clearAllSelections();
      selection.setLastSelectionSource("scene");
    }
    return true;
  };

  const applyInspectCandidateSelection = (targetType, candidate, additive = false) => {
    if (!candidate) return;
    const currentSelection = useSelectionStore.getState();
    try {
      if (targetType === "object") {
        selectionActions.setLastSelectionSource("sceneTreeObject");
      } else {
        selectionActions.setLastSelectionSource("scene");
      }
    } catch (error) {
      logger.warn("[Scene] Unexpected error during scene update", error);
    }
    if (targetType !== "mesh" || !additive) {
      clearSelectedVisualMeshes();
    }
    if (targetType === "object" && candidate.uuid) {
      if (additive) {
        selectionActions.toggleBodyInSelection(candidate.uuid);
      } else if (currentSelection.selectedBody !== candidate.uuid) {
        selectionActions.selectBody(candidate.uuid);
      }
    } else if (targetType === "body" && candidate.uuid) {
      if (additive) {
        selectionActions.toggleBodyInSelection(candidate.uuid);
      } else if (currentSelection.selectedBody !== candidate.uuid) {
        selectionActions.selectBody(candidate.uuid);
      }
    } else if (targetType === "mesh") {
      if (additive && applyVisualMeshMultiSelection(candidate)) {
        return;
      }
      if (candidate.selectionType === "geom" && candidate.name) {
        selectionActions.selectGeom(candidate.name);
      } else if (candidate.uuid) {
        selectionActions.selectMesh(candidate.uuid);
      }
    } else if (targetType === "geom" && candidate.name) {
      selectionActions.selectGeom(candidate.name);
    } else if (targetType === "inertial" && candidate.name) {
      selectionActions.selectInertial(candidate.name);
    } else if (targetType === "joint" && candidate.uuid) {
      selectionActions.selectJoint(candidate.uuid);
    } else if (targetType === "site" && candidate.name) {
      selectionActions.selectSite(candidate.name);
    } else if (targetType === "constraint" && candidate.key) {
      applyConstraintMarkerKeySelection(candidate.key);
    } else if (targetType === "actuator" && candidate.uuid) {
      selectionActions.selectActuatorInParent(
        candidate.uuid,
        candidate.parentJointUuid || null,
        candidate.parentTendonUuid || null
      );
    } else if (targetType === "camera" && candidate.uuid) {
      if (currentSelection.selectedCamera !== candidate.uuid) {
        selectionActions.selectCamera(candidate.uuid);
      }
      window.dispatchEvent(new CustomEvent("open-properties-panel"));
    } else if (targetType === "light" && candidate.uuid) {
      if (currentSelection.selectedLight !== candidate.uuid) {
        selectionActions.selectLight(candidate.uuid);
      }
      window.dispatchEvent(new CustomEvent("open-properties-panel"));
    }
  };

  const hasContextTarget = inspectCandidateTypes.some(
    (type) => !!sceneContextMenuData.candidates[type]
  );

  const getContextTargetSnapshot = (event) => {
    const clientPoint =
      Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)
        ? { x: event.clientX, y: event.clientY }
        : null;
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) {
      return {
        hasHit: false,
        owningBodyUuid: null,
        primaryTargetType: null,
        priorityPick: null,
        clientPoint,
        candidates: {
          object: null,
          body: null,
          mesh: null,
          geom: null,
          inertial: null,
          joint: null,
          site: null,
          camera: null,
          light: null,
          constraint: null,
          actuator: null,
        },
      };
    }

    const canvas = rendererRef.current.domElement;
    const rect = canvas.getBoundingClientRect();
    const pointer = new THREE.Vector2();
    const raycaster = new THREE.Raycaster();
    configureRaycasterForScenePicking(raycaster);
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, cameraRef.current);
    const intersects = raycaster.intersectObjects(sceneRef.current.children, true);

    let constraintMarkerCandidate = null;
    if (
      showConstraintMarkers &&
      constraintMarkersGroupRef.current?.visible &&
      constraintMarkerMeshesRef.current.length > 0
    ) {
      const markerHits = raycaster.intersectObjects(constraintMarkerMeshesRef.current, false);
      const markerPick = preferTendonConstraintMarkerHit(markerHits);
      const rawKey = markerPick?.object?.userData?.key ?? null;
      if (rawKey != null && rawKey !== "") {
        constraintMarkerCandidate = { key: String(rawKey) };
      }
    }

    const focusBodiesGateSet = getResolvedFocusBodiesGateSet();
    const validIntersects = intersects.filter((intersect) => {
      const obj = intersect.object;
      if (!obj.visible) return false;
      if (obj?.userData?.isConstraintVisualization) return false;
      if (
        obj?.userData?.isPositionGizmo ||
        obj?.userData?.isRotationGizmo ||
        obj?.userData?.isScaleGizmo
      )
        return false;
      if (!showCollision && isCollisionGeomObject(obj)) return false;
      if (obj.name === "sceneFloor" || obj.name === "sceneGrid" || obj.userData.isGlow) {
        return false;
      }
      if (getSavedSceneCameraPickUuid(obj)) {
        if (!showSavedCameraMarkers) return false;
        return true;
      }
      if (getSavedSceneLightPickUuid(obj)) {
        return true;
      }
      const isVisualMesh =
        obj.isMesh &&
        !isCollisionGeomObject(obj) &&
        !obj.userData.isJointEndpoint &&
        !getJointIdFromObject(obj) &&
        !obj.userData.isInertial &&
        !(obj.userData && obj.userData.type === "inertial") &&
        obj.material &&
        obj.material.color &&
        obj.material.color.getHex() !== 0xff2222 &&
        obj.material.color.getHex() !== 0xffff00;
      if (isVisualMesh) {
        if (!showVisual) return false;
        if (focusBodiesGateSet) {
          const bodyUuid = getOwningBodyUuidForObject(obj);
          return !!(bodyUuid && focusBodiesGateSet.has(bodyUuid));
        }
        return true;
      }
      if (focusBodiesGateSet) {
        const bodyUuid = getOwningBodyUuidForObject(obj);
        return !!(bodyUuid && focusBodiesGateSet.has(bodyUuid));
      }
      return true;
    });

    const primaryTarget =
      validIntersects.length > 0 ? findValidTarget(validIntersects[0]?.object || null) : null;

    const { candidates, owningBodyUuid } = collectInspectCandidatesFromIntersects(
      validIntersects,
      constraintMarkerCandidate
    );

    const hasContextTargetForSnapshot = inspectCandidateTypes.some((type) => !!candidates[type]);
    const flags = useViewerStore.getState().scenePrioritySelection;
    const priorityPick = pickPriorityInspectTarget(candidates, flags, {
      primaryTargetType: primaryTarget?.type || null,
      showVisual,
      showJoints,
      showCollision,
      showInertials,
      showSites,
      showConstraintMarkers,
    });
    return {
      hasHit: hasContextTargetForSnapshot,
      owningBodyUuid,
      primaryTargetType: primaryTarget?.type || null,
      priorityPick,
      clientPoint,
      candidates,
    };
  };

  const handleSceneContextMenuCapture = (event) => {
    if (window.isRadialClickModeActive) {
      event.preventDefault();
      setSceneContextMenuOpen(false);
      return;
    }
    if (sceneRmbContextDraggedRef.current) {
      event.preventDefault();
      event.stopPropagation();
      sceneRmbContextDraggedRef.current = false;
      sceneRmbContextAnchorRef.current = null;
      return;
    }
    sceneRmbContextAnchorRef.current = null;
    const snapshot = getContextTargetSnapshot(event);
    event.preventDefault();
    event.stopPropagation();
    setSceneContextMenuData(snapshot);
    if (!snapshot.hasHit && !isPhysicsRunning) {
      setSceneAddJointKindSubmenuOpen(false);
      setSceneAddGeomKindSubmenuOpen(false);
      setSceneAddVisualGeomKindSubmenuOpen(false);
      setActiveSubmenu("add");
    } else {
      setActiveSubmenu(null);
    }
    const menuWidth = 190;
    const menuHeight = snapshot.hasHit ? 320 : 90;
    const x = Math.max(8, Math.min(event.clientX, window.innerWidth - menuWidth - 8));
    const y = Math.max(8, Math.min(event.clientY, window.innerHeight - menuHeight - 8));
    setSceneContextMenuPos({ x, y });
    setSceneContextMenuOpen(true);
  };

  const handleContextInspectSelect = (targetType) => {
    const candidate = sceneContextMenuData.candidates[targetType];
    if (!candidate) return;
    applyInspectCandidateSelection(targetType, candidate);
    setActiveSubmenu(null);
    setSceneContextMenuOpen(false);
  };

  return {
    applyInspectCandidateSelection,
    applyVisualMeshMultiSelection,
    collectInspectCandidatesFromIntersects,
    findValidTarget,
    getCollisionTarget,
    getContextTargetSnapshot,
    getInertialTarget,
    getOwningBodyUuidForObject,
    getResolvedFocusBodiesGateSet,
    getSiteTarget,
    getVisualGeomTarget,
    handleContextInspectSelect,
    handleSceneContextMenuCapture,
    hasContextTarget,
    isCollisionGeomObject,
  };
}
